import dayjs from '~/timeInit';

import type { CppInvoiceNote, CppInvoiceNoteCode } from '~/services/LuxLoyaltyService.types';

import { formatCurrencyCents, formatSignedPoints } from './invoiceFormat';

/** Transaction ids listed in full before the rest are summarised as a count. */
const MAX_LISTED_IDS = 3;

/** Rendered copy for one blocker or warning. */
export interface InvoiceNoteCopy {
  /** What is wrong, prefixed with the day when the note concerns one. */
  headline: string;
  /** Specifics drawn from the note's `detail` payload; null when it carried nothing usable. */
  detail: string | null;
}

/**
 * Headline copy per code.
 *
 * The service returns `code` as a literal union precisely so consumers branch on it rather than
 * render raw enum names at an admin, so every code must be represented here — `Record` makes a new
 * code a compile error rather than a blank line in the UI.
 */
const HEADLINES: Record<CppInvoiceNoteCode, string> = {
  UNPRICED_DAY: 'No rate card covered this day, so its activity could not be priced.',
  UNPRICED_REFUND_ORIGIN: 'A refund could not be priced against the transaction it reverses.',
  PERIOD_NOT_SETTLED: 'This period has not finished settling — the figures will still move.',
  NEGATIVE_DAY: 'This day priced to a negative amount.',
  PARTIAL_CARD_COVERAGE: 'Rate cards cover only part of this period.',
  REFUND_EXCLUDED_TIERED_CARD: 'A refund was excluded because it reverses activity priced on a tiered rate card.',
  TRANSACTION_MISSING_PROCESSED_AT: 'Some transactions have no processed date and could not be placed on a day.',
};

/**
 * `detail` is one generic `Record<string, unknown>` shared by every code — the schema cannot
 * narrow it — so each field is read defensively. A payload that is absent, or carries a field of
 * the wrong type, degrades to no detail line rather than rendering `undefined` at an admin.
 */
function readNumber(detail: Record<string, unknown> | undefined, key: string): number | null {
  const value = detail?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readString(detail: Record<string, unknown> | undefined, key: string): string | null {
  const value = detail?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readStringArray(detail: Record<string, unknown> | undefined, key: string): Array<string> | null {
  const value = detail?.[key];
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string') ? value : null;
}

/** Formats a `YYYY-MM-DD` day for display. */
function formatDay(date: string): string {
  return dayjs(date).format('DD MMM YYYY');
}

/** Pluralises a transaction count. */
function transactions(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? 'transaction' : 'transactions'}`;
}

/** Lists ids, summarising the tail once there are more than {@link MAX_LISTED_IDS}. */
function listIds(ids: Array<string>): string {
  if (ids.length <= MAX_LISTED_IDS) {
    return ids.join(', ');
  }
  return `${ids.slice(0, MAX_LISTED_IDS).join(', ')} and ${ids.length - MAX_LISTED_IDS} more`;
}

/** Builds the detail line for a note, or null when its payload carried nothing usable. */
function buildDetail(note: CppInvoiceNote, currency: string | null): string | null {
  const { detail } = note;

  switch (note.code) {
    case 'UNPRICED_DAY': {
      const count = readNumber(detail, 'transactionCount');
      const netPoints = readNumber(detail, 'netPoints');
      if (count === null || netPoints === null) {
        return null;
      }
      return `${transactions(count)}, ${formatSignedPoints(netPoints)} net points affected.`;
    }

    case 'UNPRICED_REFUND_ORIGIN': {
      const refundId = readString(detail, 'refundTransactionId');
      if (!refundId) {
        return null;
      }
      if (readString(detail, 'reason') === 'ORIGIN_NOT_FOUND') {
        return `Refund ${refundId} names no transaction that exists.`;
      }
      const originalDate = readString(detail, 'originalDate');
      const reverses = originalDate ? ` from ${formatDay(originalDate)}` : '';
      return `Refund ${refundId} reverses a transaction${reverses} that itself could not be priced.`;
    }

    case 'PERIOD_NOT_SETTLED': {
      const pending = readNumber(detail, 'pendingTransactionCount');
      return pending === null ? null : `${transactions(pending)} are still pending.`;
    }

    case 'NEGATIVE_DAY': {
      const amountCents = readNumber(detail, 'amountCents');
      return amountCents === null ? null : `This day priced to ${formatCurrencyCents(amountCents, currency)}.`;
    }

    case 'PARTIAL_CARD_COVERAGE': {
      const start = readString(detail, 'effectiveStart');
      if (!start) {
        return null;
      }
      const end = readString(detail, 'effectiveEnd');
      const window = end ? `${formatDay(start)} – ${formatDay(end)}` : `${formatDay(start)} onwards`;
      return `The rate card covers ${window}.`;
    }

    case 'REFUND_EXCLUDED_TIERED_CARD': {
      const refundId = readString(detail, 'refundTransactionId');
      if (!refundId) {
        return null;
      }
      const originalDate = readString(detail, 'originalDate');
      const reverses = originalDate ? ` from ${formatDay(originalDate)}` : '';
      return `Refund ${refundId} reverses a transaction${reverses} priced on a tiered card.`;
    }

    case 'TRANSACTION_MISSING_PROCESSED_AT': {
      const ids = readStringArray(detail, 'transactionIds');
      return ids === null || ids.length === 0 ? null : `${transactions(ids.length)} affected: ${listIds(ids)}.`;
    }

    default:
      return null;
  }
}

/**
 * Renders a blocker or warning as readable, actionable copy.
 *
 * The day prefix is not guaranteed to fall inside the invoiced period — a refund is priced against
 * the day it reverses, which can predate the period — so it is rendered as stated rather than
 * matched against the invoice's own days.
 *
 * @param note - The note as returned in `blockers` or `warnings`.
 * @param currency - The invoice currency, for notes whose detail carries an amount.
 */
export function describeInvoiceNote(note: CppInvoiceNote, currency: string | null = null): InvoiceNoteCopy {
  const headline = HEADLINES[note.code] ?? note.code;

  return {
    headline: note.date ? `${formatDay(note.date)}: ${headline}` : headline,
    detail: buildDetail(note, currency),
  };
}
